/**
 * Onboarding / user-guide popup + floating help button.
 *
 * Behaviour:
 *  - The button (bottom-right FAB) is always mounted on authenticated pages.
 *  - The modal auto-opens after login when the guide `version` is higher
 *    than the current user's `guide_dismissed_version`.
 *  - "Non mostrare più" bumps the dismissal to the current version; the
 *    popup won't reopen until the super-admin publishes a new version.
 *  - Any block can link to internal deep-dive pages (rendered inline in a
 *    second dialog layer).
 */
import React, { useCallback, useEffect, useState } from "react";
import { HelpCircle, X, ChevronRight, Play } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const isVideo = (url) => /\.(mp4|webm|mov)$/i.test(url || "");

const BlockMedia = ({ url }) => {
  if (!url) return null;
  if (isVideo(url)) {
    return (
      <video
        src={url}
        controls
        preload="metadata"
        className="w-full max-h-[420px] rounded-2xl bg-black object-cover shadow-md"
      />
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      className="w-full max-h-[420px] rounded-2xl object-cover shadow-md"
    />
  );
};

const GuidePagePanel = ({ slug, onClose }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/guide/pages/${slug}`)
      .then((r) => { if (!cancel) setPage(r.data); })
      .catch(() => { if (!cancel) setPage({ title: "Pagina non trovata", content: "" }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [slug]);
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm" data-testid="guide-page-panel">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="font-display font-bold text-xl tracking-tight">{page?.title || "…"}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Chiudi" data-testid="guide-page-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto whitespace-pre-wrap text-slate-700 leading-relaxed">
          {loading ? <div className="text-slate-400">Caricamento…</div> : (page?.content || <em>Contenuto non disponibile.</em>)}
        </div>
      </div>
    </div>
  );
};

const GuideModal = ({ open, onClose, guide, onDismissForever }) => {
  const [openSlug, setOpenSlug] = useState(null);
  if (!open || !guide) return null;
  const blocks = guide.blocks || [];
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4" data-testid="guide-modal">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Guida ImmoMap</div>
            <h2 className="font-display font-bold text-2xl tracking-tight mt-1" data-testid="guide-title">
              {guide.title || "Benvenuto"}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Chiudi" data-testid="guide-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
          {blocks.length === 0 && (
            <div className="p-10 text-center text-slate-500">
              Nessun contenuto disponibile. L'amministratore non ha ancora pubblicato la guida.
            </div>
          )}
          {blocks.map((b, idx) => (
            <div key={b.id || idx} className="p-6 md:p-8 space-y-4" data-testid={`guide-block-${idx}`}>
              <BlockMedia url={b.image_url} />
              {b.subtitle && (
                <h3 className="font-display font-bold text-xl tracking-tight text-slate-900">{b.subtitle}</h3>
              )}
              {b.description && (
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{b.description}</p>
              )}
              {Array.isArray(b.links) && b.links.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {b.links.map((l, li) => (
                    <button
                      key={li}
                      onClick={() => setOpenSlug(l.slug)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-[#0A84FF] hover:text-[#0A84FF] transition px-4 h-9 text-sm font-medium text-slate-700 bg-white"
                      data-testid={`guide-block-${idx}-link-${li}`}
                    >
                      {isVideo(l.slug) ? <Play className="w-4 h-4" /> : null}
                      {l.label || l.slug}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 sticky bottom-0 bg-white">
          <button
            onClick={onDismissForever}
            className="text-sm text-slate-500 hover:text-slate-900 underline underline-offset-4"
            data-testid="guide-dismiss"
          >
            Non mostrare più questa versione
          </button>
          <Button onClick={onClose} className="rounded-full bg-[#0A84FF]" data-testid="guide-continue">
            Chiudi
          </Button>
        </div>
      </div>
      {openSlug && <GuidePagePanel slug={openSlug} onClose={() => setOpenSlug(null)} />}
    </div>
  );
};

const GuideLauncher = () => {
  const { user, loading: authLoading } = useAuth();
  const [guide, setGuide] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchGuide = useCallback(async (autoOpen = false) => {
    try {
      const r = await api.get("/guide");
      setGuide(r.data.guide);
      if (autoOpen && r.data.should_show) setOpen(true);
    } catch (e) { /* silent — guide is optional */ }
  }, []);

  useEffect(() => {
    if (!authLoading && user) fetchGuide(true);
  }, [authLoading, user, fetchGuide]);

  const handleDismiss = async () => {
    try { await api.post("/guide/dismiss"); } catch {}
    setOpen(false);
  };

  if (authLoading || !user) return null;

  return (
    <>
      <button
        onClick={() => { fetchGuide(false); setOpen(true); }}
        className="fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full bg-gradient-to-br from-[#0A84FF] to-[#12466F] text-white shadow-2xl hover:scale-105 active:scale-95 transition flex items-center justify-center"
        aria-label="Guida"
        title="Guida"
        data-testid="guide-fab"
      >
        <HelpCircle className="w-6 h-6" strokeWidth={1.8} />
      </button>
      <GuideModal
        open={open}
        onClose={() => setOpen(false)}
        guide={guide}
        onDismissForever={handleDismiss}
      />
    </>
  );
};

export default GuideLauncher;
